import { createMiddleware } from '@tanstack/react-start';
import {
  getCookie,
  getRequestHeader,
  setCookie,
} from '@tanstack/react-start/server';

import {
  AD_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS,
  AD_ATTRIBUTION_COOKIE_NAME,
  extractAdAttributionTouch,
  mergeAdAttribution,
  parseAdAttributionCookie,
  serializeAdAttributionCookie,
} from '@/utils/ad-attribution';

import { env } from '../../env';

/**
 * A "touch" only matters for document navigations — asset requests, XHR/fetch
 * calls, and server-function POSTs should never trigger a Set-Cookie. Modern
 * browsers send `Sec-Fetch-Dest: document` on the real thing; when that
 * header is absent (older browsers, non-browser clients, tests) fall back to
 * the same Accept-header check TanStack Start's own router uses to decide a
 * request wants HTML.
 */
export function isDocumentRequest(request: Request): boolean {
  if (request.method !== 'GET' && request.method !== 'HEAD') {
    return false;
  }

  const dest = request.headers.get('sec-fetch-dest');
  if (dest) {
    return dest === 'document';
  }

  const accept = request.headers.get('accept') ?? '';
  return accept.includes('text/html') || accept.includes('*/*');
}

/**
 * `Domain=.respondent.io` is silently dropped by the browser unless the
 * request host is actually a respondent.io subdomain, so local/dev hosts
 * (localhost, rio.test, rio.localhost) need a host-only cookie instead. Same
 * IS_LOCAL split the session cookie already uses for its `secure` flag
 * (utils/session/constants.ts) — dev never has a real HTTPS respondent.io
 * host to scope to.
 */
function attributionCookieDomain(): string | undefined {
  return env.IS_LOCAL ? undefined : '.respondent.io';
}

/**
 * Global request middleware (registered in `src/start.ts` so it runs on
 * every route, including the public, unauthenticated screener-apply
 * surface): on a document request carrying at least one ad click ID, merges
 * it into the first-party `rio_attribution` cookie using last-touch-per-
 * platform semantics. No-op for every other request, including document
 * requests with no click ID — an existing cookie is left completely alone.
 */
export const adAttributionMiddleware = createMiddleware().server(
  ({ request, next }) => {
    if (!isDocumentRequest(request)) {
      return next();
    }

    const url = new URL(request.url);
    const clickIdTouch = extractAdAttributionTouch(url.searchParams);
    if (Object.keys(clickIdTouch).length === 0) {
      return next();
    }

    const existing = parseAdAttributionCookie(
      getCookie(AD_ATTRIBUTION_COOKIE_NAME),
    );
    const merged = mergeAdAttribution(existing, clickIdTouch, {
      requestUrl: url.toString(),
      referrer: getRequestHeader('referer'),
      capturedAt: new Date(),
    });

    setCookie(
      AD_ATTRIBUTION_COOKIE_NAME,
      serializeAdAttributionCookie(merged),
      {
        domain: attributionCookieDomain(),
        path: '/',
        maxAge: AD_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS,
        sameSite: 'lax',
        secure: !env.IS_LOCAL,
        httpOnly: false,
      },
    );

    return next();
  },
);
